/**
 * ConsentSignal — Interface Contract
 * ====================================
 * The interface any consent UI must implement to integrate with the telemetry layer.
 * Defined in `packages/domain` so it can be consumed without platform dependencies.
 *
 * Rules:
 *   - Must be callable before the user is authenticated.
 *   - Must not throw; return 'unknown' on any read error.
 *   - subscribe() MUST call the callback synchronously with the current state on
 *     registration (so the telemetry layer can initialize without a race condition).
 */

export type ConsentStateValue = 'granted' | 'revoked' | 'unknown';

export interface ConsentSignal {
  /**
   * Returns the current synchronous consent state.
   * Returns 'unknown' on any read error (never throws).
   */
  getState(): ConsentStateValue;

  /**
   * Registers a callback invoked synchronously on registration with the current
   * state and again on every subsequent state change.
   *
   * @returns An unsubscribe function. Call it to stop receiving updates.
   */
  subscribe(cb: (state: ConsentStateValue) => void): () => void;
}

/** localStorage key used to persist consent state across sessions. */
export const CONSENT_STORAGE_KEY = 'telemetry_consent' as const;

// ─── Cookie Consent (v1) ──────────────────────────────────────────────────────

/** Union of all supported cookie category names. */
export type CookieCategoryName = 'essential' | 'analytics';

/** Per-category accepted/declined decisions. `essential` is always `true`. */
export type CookieCategoryDecisions = {
  essential: true;
  analytics: boolean;
};

/** Structured consent record persisted to `localStorage['cookie_consent']`. */
export interface CookieConsentRecord {
  version: 1;
  decidedAt: number; // Unix ms timestamp
  categories: CookieCategoryDecisions;
}

/** localStorage key for the granular consent record. */
export const COOKIE_CONSENT_STORAGE_KEY = 'cookie_consent' as const;

/** Schema version — bump when `CookieConsentRecord` shape changes. */
export const COOKIE_CONSENT_SCHEMA_VERSION = 1 as const;
